import { DependencyResolver } from '@bingads-webui-campaign/dependency-resolver';
import $ from 'jquery';
import { CashbackPercentControlView } from '@bingads-webui-campaign/cashback-percent-control-view';
import _ from 'underscore';
import {
  ageSettingOptions,
  genderSettingOptions,
  deviceSettingOptions,
  companyTargetItemTemplate
} from '@bingads-webui-campaign/audience-campaign-shared';
import { getBidAdjustmentOptions } from './bid-adjustment-utils';
import targetExcludeTemplate from './target-exclude.pug';
import bidAdjustmentTemplate from './bid-adjustment.pug';

export class GridConfigGenerator {
  constructor(options = {}) {
    this.data = options.data;
    this.criterionType = options.criterionType;
    this.readonly = options.readonly;
    this.showExclusion = options.showExclusion;
    this.activityLogger = options.activityLogger;
    this.bingadsGridColumn = DependencyResolver.get('component/bingads-grid/shared/column');
    this.i18n = DependencyResolver.get('component/i18n/loader!campaignui/feature/audience-campaign');
    this.decimal = DependencyResolver.get('component/humanize/decimal');

    const permissionConfig = DependencyResolver.get('component/config/index');
    const permissions = permissionConfig.get('Permissions');

    this.supportCashbackSettings = options.supportCashbackSettings &&
      !permissions.IsPersonalizedOffersV3Enabled;
    this.viewContext = options.viewContext;
    this.onCashbackPercentChange = options.onCashbackPercentChange || _.noop;
    this.$gridRoot = options.$gridRoot;
    this.isMSANBoostEnabled = permissions.IsAmplifyEnabled;
    this.isHideBidAdjustmentForBoostEnabled = permissions.dynamic && permissions.dynamic.HideBidAdjustmentForBoost;
  }

  get advancedTargetExcludedColumn() {
    return this.bingadsGridColumn({
      name: 'TargetExclude',
      property: item => item,
      title: this.showExclusion ? this.i18n.getString('Audience_Campaign_Target_Setting_Company_Grid_Column_Target') : '',
      template: (locals) => {
        const criterion = locals.value;
        const isExcluded = this.isCriterionExcluded(criterion);

        return targetExcludeTemplate({
          isIncluded: !isExcluded,
          isExcluded,
          i18n: this.i18n,
        });
      },
    });
  }

  get audienceTargetColumn() {
    return this.bingadsGridColumn({
      i18n: this.i18n,
      name: 'Target',
      property: 'Criterion',
      title: 'Audience_Campaign_Target_Setting_Audience_Title',
      template: locals => locals.value.AudienceName,
    });
  }

  get cashbackPercentColumn() {
    return this.bingadsGridColumn({
      i18n: this.i18n,
      name: 'CashbackPercent',
      property: item => item,
      title: 'Campaign_Maximum_Cashback_Offer',
      bodyClasses: ['cashback-percent-cell-wrap'],
      template: (locals) => {
        const criterion = locals.value;

        if (this.isCriterionExcluded(locals.value)) {
          return '-';
        }

        const { CashbackPercent } = criterion.Criterion;

        const cashbackPercentControlView = new CashbackPercentControlView({
          viewContext: this.viewContext,
          i18n: this.i18n,
          decimal: this.decimal,
          CashbackPercent,
          isParentLevel: this.readonly,
        });

        setTimeout(() => {
          // eslint-disable-next-line no-underscore-dangle
          const key = locals.value.Id;
          const row = this.$gridRoot.find(`tr[data-key='${key}']`);
          const root = $(row).find('.cashback-percent-cell-wrap');
          root.empty().append(cashbackPercentControlView.render().$el);
          cashbackPercentControlView.on('change', (validated, val) => {
            this.onCashbackPercentChange(validated, val, row);
          });
        });

        return '';
      },
    });
  }

  get advancedBidAdjustmentColumn() {
    return this.bingadsGridColumn({
      i18n: this.i18n,
      name: 'BidAdjustment',
      property: item => item,
      title: 'Audience_Campaign_Settings_BidAdjustment',
      template: (locals) => {
        const criterion = locals.value;

        if (this.isCriterionExcluded(locals.value)) {
          return '-';
        }

        const bidAdjustmentOptions = getBidAdjustmentOptions();

        let bidValue = 0;

        // For Audience Criterions
        if (criterion.Criterion.BidMultiplier != null) {
          bidValue = criterion.Criterion.BidMultiplier;
        }

        // For ones that are generated
        if (criterion.Criterion.BidAdjustment != null) {
          bidValue = criterion.Criterion.BidAdjustment;
        }

        bidAdjustmentOptions.percentage = Math.abs(bidValue);

        // negtive or -0
        if ((bidValue < 0) || (1 / bidValue === -Infinity)) {
          bidAdjustmentOptions.selectedOption = 'dec';
        }

        return bidAdjustmentTemplate({
          size: 5, // input control size
          percentage: bidAdjustmentOptions.percentage,
          availableOptions: bidAdjustmentOptions.availableOptions,
          selectedOption: bidAdjustmentOptions.selectedOption,
          readonly: this.readonly,
          i18n: this.i18n,
        });
      },
    });
  }

  isCriterionExcluded(criterion) {
    let isExcluded = false;

    if (criterion.Status && criterion.Status === 'Excluded') {
      isExcluded = true;
    }

    if (criterion.SelectionStatus && criterion.SelectionStatus === 'Excluded') {
      isExcluded = true;
    }

    // Linked in target knows the excluded state by the type.
    if (criterion.CriterionType && criterion.CriterionType.indexOf('Negative') >= 0) {
      isExcluded = true;
    }

    return isExcluded;
  }

  getTargetColumnByCriterionType() {
    switch (this.criterionType) {
      case 'Audience':
        return this.audienceTargetColumn;
      case 'Age':
      case 'Gender':
      case 'Device':
        return this.getAdvancedTargetColumn(this.criterionType);
      case 'Location':
      case 'NegativeLocation':
      case 'Radius':
        return this.locationTargetColumn;
      case 'CompanyName':
      case 'NegativeCompanyName':
        return this.companyTargetColumn;
      case 'JobFunction':
      case 'NegativeJobFunction':
        return this.jobFunctionTargetColumn;
      case 'Industry':
      case 'NegativeIndustry':
        return this.industryTargetColumn;
      default:
        throw new Error('unsupported criterion type');
    }
  }

  getColumns() {
    return _.compact([
      this.advancedTargetExcludedColumn,
      this.getTargetColumnByCriterionType(),
      !(this.isMSANBoostEnabled && this.isHideBidAdjustmentForBoostEnabled) && this.advancedBidAdjustmentColumn,
      this.supportCashbackSettings && this.cashbackPercentColumn,
    ]);
  }

  getAdvancedTargetColumn(criterionType) {
    return this.bingadsGridColumn({
      i18n: this.i18n,
      name: 'Target',
      property: 'Criterion',
      title: `Audience_Campaign_Target_Setting_${criterionType}_Title`,
      template: (locals) => {
        if (criterionType === 'Age') {
          // When retrieved from MT, AgeRange field is there and we respect that first.
          const ageTarget =
            locals.value.AgeRange ? locals.value.AgeRange : locals.value.AgeCriterion.Target;
          const ageTargetLabel = ageSettingOptions.selections[ageTarget].labelKey;

          return `${this.i18n.getString(ageTargetLabel)}`;
        } else if (criterionType === 'Gender') {
          // When retrieved from MT, GenderType field is there and we respect that first.
          const genderTarget =
            locals.value.GenderType ? locals.value.GenderType : locals.value.GenderCriterion.Target;
          const genderTargetLabel = genderSettingOptions.selections[genderTarget].labelKey;

          return `${this.i18n.getString(genderTargetLabel)}`;
        } else if (criterionType === 'Device') {
          // When retrieved from MT, DeviceType field is there and we respect that first.
          const deviceTarget =
            locals.value.DeviceName ?
              locals.value.DeviceName : locals.value.DeviceCriterion.DeviceName;
          const deviceTargetLabel = deviceSettingOptions.selections[deviceTarget].labelKey;

          return `${this.i18n.getString(deviceTargetLabel)}`;
        }

        return '';
      },
    });
  }

  get locationTargetColumn() {
    return this.bingadsGridColumn({
      i18n: this.i18n,
      name: 'Target',
      property: 'Criterion',
      title: 'Audience_Campaign_Target_Setting_Location_Title',
      template: (locals) => {
        let locationTarget;
        if (locals.value.RadiusCriterion) {
          locationTarget = locals.value.RadiusCriterion.Name;
        } else if (locals.value.LocationCriterion) {
          locationTarget = locals.value.LocationCriterion.Name;
        } else if (locals.value.Name) {
          locationTarget = locals.value.Name;
        } else {
          if (this.activityLogger) {
            this.activityLogger.signal(`Criterion value: ${JSON.stringify(locals.value)}`, 'bid adjustment locationTargetColumn');
          }
          return null;
        }

        return locationTarget;
      },
    });
  }

  get companyTargetColumn() {
    return this.bingadsGridColumn({
      i18n: this.i18n,
      name: 'Target',
      property: 'Criterion',
      title: 'Audience_Campaign_Target_Setting_Company_Grid_Column_Companies',
      template: (locals) => {
        const criterion = locals.value.CompanyNameCriterion;
        const item = {
          name: criterion.Name,
          logoUrl: criterion.LogoUrl,
          industry: criterion.Industry,
          size: criterion.Size,
        };
        return companyTargetItemTemplate({ item, i18n: this.i18n });
      },
    });
  }

  get jobFunctionTargetColumn() {
    return this.bingadsGridColumn({
      i18n: this.i18n,
      name: 'Target',
      property: 'Criterion',
      title: 'Audience_Campaign_Target_Setting_Job_Function_Grid_Column_Job_Functions',
      template: (locals) => {
        const criterion = locals.value.JobFunctionCriterion;

        return criterion.Name;
      },
    });
  }

  get industryTargetColumn() {
    return this.bingadsGridColumn({
      i18n: this.i18n,
      name: 'Target',
      property: 'Criterion',
      title: 'Audience_Campaign_Target_Setting_Industry_Grid_Column_Industries',
      template: (locals) => {
        const criterion = locals.value.IndustryCriterion;

        return criterion.Name;
      },
    });
  }

  getConfig() {
    return {
      tableClasses: ['table', 'table--no-border', 'grid'],
      dataSource: {
        type: 'memory',
        data: this.data,
        primaryKey: 'Id',
      },
      columns: this.getColumns(),
    };
  }
}
